<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  newEmail: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'update:newEmail', 'validEmailTyped'])

const localNewEmail = ref(props.newEmail)
const emailInput = ref(null)

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const isValidEmail = computed(() => {
  return localNewEmail.value.length > 0 && emailRegex.test(localNewEmail.value)
})

// Watch for changes in validation state and emit accordingly
watch(isValidEmail, (newValue) => {
  emit('validEmailTyped', newValue)
})

// Watch for changes in the input value
watch(localNewEmail, (newValue) => {
  emit('update:newEmail', newValue)
  // Also emit validation state when input changes
  const isValid = newValue.length > 0 && emailRegex.test(newValue)
  emit('validEmailTyped', isValid)
})

// Watch for prop changes
watch(() => props.newEmail, (newValue) => {
  localNewEmail.value = newValue
})

function addEmail(email) {
  if (email !== '' && emailRegex.test(email)) {
    const updatedEmails = [...props.modelValue, email]
    emit('update:modelValue', updatedEmails)
  }
}

function removeEmail(index) {
  const updatedEmails = props.modelValue.filter((_, i) => i !== index)
  emit('update:modelValue', updatedEmails)
  
  // After removing an email, check if the current input is valid
  // and emit the validation state
  const isCurrentInputValid = localNewEmail.value.length > 0 && emailRegex.test(localNewEmail.value)
  emit('validEmailTyped', isCurrentInputValid)
  
  focusInput()
}

function commitEmail(refocus = false) {
  localNewEmail.value = localNewEmail.value.trim().replace(/,$/, '')
  if (isValidEmail.value && localNewEmail.value.length > 0) {
    addEmail(localNewEmail.value)
    localNewEmail.value = ''
    emit('validEmailTyped', false)
    if (refocus) {
      setTimeout(() => {
        focusInput()
      })
    }
  }
}

function checkEmail(event) {
  if (event.key === 'Tab' || event.key === 'Enter' || event.key === ',') {
    event.preventDefault()
    commitEmail(true)
  }
}

function handleBlur() {
  commitEmail(false)
}

function focusInput() {
  if (emailInput.value) {
    emailInput.value.focus()
  }
}

defineExpose({
  focusInput,
  addEmail
})
</script>

<template>
  <div
    class="email-input-field"
    :class="{
      'not-valid': !isValidEmail && localNewEmail.length !== 0
    }">
    <div class="valid-email-container" @click="focusInput">
      <div v-for="(email, index) in modelValue" :key="index" class="valid-email">
        {{ email }}
        <i @click.stop="removeEmail(index)" class="bi bi-x-lg"></i>
      </div>
      <input
        ref="emailInput"
        type="text"
        v-model="localNewEmail"
        @keyup="checkEmail"
        @keydown.tab="checkEmail"
        @blur="handleBlur"
        class="email-input"
        :placeholder="placeholder" />
    </div>
  </div>
</template>
  
<style scoped lang="scss">
  .email-input-field {
    border: 1px solid var(--bs-gray-700);
    padding: 10px;
    border-radius: 4px;
  
    &.not-valid {
      border-color: var(--bs-form-invalid-border-color);
      padding-right: calc(1.5em + 0.75rem);
    }
  }
  
  .valid-email-container {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    height: auto;
    cursor: text;
  }
  
  .valid-email {
    padding: 6px 12px 6px 12px;
    background-color: #f7f1fd;
    border: 1px solid #5c0cb8;
    border-radius: 40px;
    color: #5c0cb8;
    display: flex;
    align-items: center;
    gap: 15px;
    font-size: 14px;
  
    i {
      cursor: pointer;
      -webkit-text-stroke: 1px;
    }
  }
  
  .email-input {
    border: none;
    background-color: transparent;
    outline: none;
    flex-grow: 1;
    min-width: 50px;
    font-size: 14px;
  }

  .email-input::placeholder {
    color: var(--bs-gray-600);
    font-size: 16px;
  }
</style>